import React, { useEffect, useRef, useState } from "react";

// ===========================
// CONSTANTS
// ===========================
const APPLE_URL = "https://apps.apple.com/us/app/smpl-golf/id6746171849";
const GOOGLE_URL =
  "https://play.google.com/store/apps/details?id=com.smplgolf.smplgolf";
const SUPABASE_URL = "https://jvtpywnhigkrvgfiwazb.supabase.co";
const SUPABASE_ANON_KEY =
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imp2dHB5d25oaWdrcnZnZml3YXpiIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTAxMTU2MDksImV4cCI6MjA2NTY5MTYwOX0.eeAZfbO6S2q526RMKL-Q8evRMBjKGrNuKTL2Y0kgLcc";

const SCREENSHOTS = [
  "/img01.png",
  "/img02.png",
  "/img03.png",
  "/img04.png",
  "/img05.png",
  "/img06.png",
  "/img07.png",
  "/img08.png",
];

const FEATURES = [
  {
    icon: "🏌️",
    title: "Simple Swing",
    body: "Drag to aim, swipe down to swing. Intuitive controls anyone can pick up in seconds.",
    bg: "#dcfce7",
  },
  {
    icon: "⛳",
    title: "Real Course Strategy",
    body: "Wind, spin, bunkers, trees, and slope — every shot is a decision that matters.",
    bg: "#dbeafe",
  },
  {
    icon: "⚡",
    title: "Rounds in Minutes",
    body: "Play a full 18-hole round in under 10 minutes. Progress saves after every swing.",
    bg: "#fef9c3",
  },
  {
    icon: "🌀",
    title: "Realistic Ball Physics",
    body: "Lie, terrain, and spin affect every shot just like the real thing.",
    bg: "#fce7f3",
  },
  {
    icon: "🏆",
    title: "Global Leaderboards",
    body: "Coming soon — compete with players worldwide and climb the ranks.",
    bg: "#ffedd5",
  },
];

// ===========================
// PHONE MOCKUP
// ===========================
function PhoneMockup({ children, width = 270 }) {
  return (
    <div
      style={{
        width,
        background: "#1c1c1e",
        borderRadius: 44,
        padding: 10,
        boxShadow:
          "0 50px 100px rgba(0,0,0,0.35), inset 0 0 0 1px rgba(255,255,255,0.12)",
        position: "relative",
        flexShrink: 0,
      }}
    >
      {/* Side buttons (decorative) */}
      <div
        style={{
          position: "absolute",
          left: -3,
          top: 84,
          width: 3,
          height: 30,
          background: "#2c2c2e",
          borderRadius: "2px 0 0 2px",
        }}
      />
      <div
        style={{
          position: "absolute",
          left: -3,
          top: 126,
          width: 3,
          height: 54,
          background: "#2c2c2e",
          borderRadius: "2px 0 0 2px",
        }}
      />
      <div
        style={{
          position: "absolute",
          left: -3,
          top: 192,
          width: 3,
          height: 54,
          background: "#2c2c2e",
          borderRadius: "2px 0 0 2px",
        }}
      />
      <div
        style={{
          position: "absolute",
          right: -3,
          top: 150,
          width: 3,
          height: 70,
          background: "#2c2c2e",
          borderRadius: "0 2px 2px 0",
        }}
      />
      {/* Screen */}
      <div
        style={{
          borderRadius: 36,
          overflow: "hidden",
          background: "#000",
          position: "relative",
        }}
      >
        {/* Notch */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: "50%",
            transform: "translateX(-50%)",
            width: 88,
            height: 26,
            background: "#1c1c1e",
            borderRadius: "0 0 18px 18px",
            zIndex: 10,
          }}
        />
        {children}
      </div>
    </div>
  );
}

// ===========================
// STORE BUTTONS
// ===========================
function StoreButtons({ size = 54, centered = false }) {
  return (
    <div
      style={{
        display: "flex",
        gap: 14,
        flexWrap: "wrap",
        justifyContent: centered ? "center" : "flex-start",
      }}
    >
      <a
        href={APPLE_URL}
        target="_blank"
        rel="noopener noreferrer"
        style={{ display: "block" }}
      >
        <img
          src="/apple-badge.png"
          alt="Download on the App Store"
          style={{ height: size, display: "block" }}
        />
      </a>
      <a
        href={GOOGLE_URL}
        target="_blank"
        rel="noopener noreferrer"
        style={{ display: "block" }}
      >
        <img
          src="/google-play-badge.png"
          alt="Get it on Google Play"
          style={{ height: size, display: "block" }}
        />
      </a>
    </div>
  );
}

// ===========================
// MAIN APP
// ===========================
export default function App() {
  const [current, setCurrent] = useState(0);
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState(null);
  const videoRef = useRef(null);
  const supabaseRef = useRef(null);

  // Load Google Fonts
  useEffect(() => {
    document.documentElement.style.scrollBehavior = "smooth";
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap";
    document.head.appendChild(link);
  }, []);

  // Autoplay hero video
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.play().catch(() => {});
    }
  }, []);

  // Auto-advance screenshot carousel
  useEffect(() => {
    const t = setInterval(
      () => setCurrent((i) => (i + 1) % SCREENSHOTS.length),
      3600,
    );
    return () => clearInterval(t);
  }, []);

  // Supabase lazy-load
  const ensureSupabase = () =>
    new Promise((resolve, reject) => {
      if (supabaseRef.current) return resolve(supabaseRef.current);
      const w = window;
      const finalize = () => {
        try {
          if (!w.supabase) return reject(new Error("supabase CDN not loaded"));
          supabaseRef.current = w.supabase.createClient(
            SUPABASE_URL,
            SUPABASE_ANON_KEY,
          );
          resolve(supabaseRef.current);
        } catch (err) {
          reject(err);
        }
      };
      if (w.supabase) return finalize();
      const script = document.createElement("script");
      script.src = "https://cdn.jsdelivr.net/npm/@supabase/supabase-js@2";
      script.async = true;
      script.onload = finalize;
      script.onerror = () => reject(new Error("Failed to load supabase"));
      document.head.appendChild(script);
    });

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleUpsert = async () => {
    setStatus(null);
    const trimmed = email.trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setStatus({ type: "error", msg: "Please enter a valid email." });
      return;
    }
    try {
      const supabase = await ensureSupabase();
      const { error } = await supabase
        .from("email_list")
        .upsert({ email: trimmed })
        .select();
      if (error) throw error;
      setStatus({
        type: "ok",
        msg: "🎉 You're on the list! We'll be in touch.",
      });
      setEmail("");
    } catch (err) {
      setStatus({ type: "error", msg: err.message || "Something went wrong." });
    }
  };

  return (
    <div
      style={{
        fontFamily: "'Inter', system-ui, -apple-system, Segoe UI, sans-serif",
        color: "#111827",
        overflowX: "hidden",
      }}
    >
      {/* ========== NAVBAR ========== */}
      <header
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 100,
          height: 72,
          background: "rgba(255,255,255,0.92)",
          backdropFilter: "saturate(180%) blur(14px)",
          borderBottom: "1px solid rgba(0,0,0,0.07)",
        }}
      >
        <div
          style={{
            maxWidth: 1200,
            margin: "0 auto",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0 24px",
          }}
        >
          <img src="/smplLogo.png" alt="SMPL Golf" style={{ height: 54 }} />
          <nav
            className="nav-links"
            style={{ display: "flex", gap: 28, alignItems: "center" }}
          >
            <button onClick={() => scrollTo("features")} className="nav-btn">
              Features
            </button>
            <button onClick={() => scrollTo("gallery")} className="nav-btn">
              Gallery
            </button>
            <button onClick={() => scrollTo("email")} className="nav-btn">
              Updates
            </button>
            <a
              href={APPLE_URL}
              target="_blank"
              rel="noopener noreferrer"
              // className="cta-pill"
            >
              <img
                src="/apple-badge.png"
                alt="Download on the App Store"
                style={{ height: 40, display: "block" }}
              />
            </a>
            <a
              href={GOOGLE_URL}
              target="_blank"
              rel="noopener noreferrer"
              // className="cta-pill"
            >
              <img
                src="/google-play-badge.png"
                alt="Get it on Google Play"
                style={{ height: 54, display: "block" }}
              />
            </a>
          </nav>
        </div>
      </header>

      {/* ========== HERO ========== */}
      <section
        style={{
          minHeight: "100vh",
          background:
            "linear-gradient(150deg, #f0fdf4 0%, #ffffff 50%, #ecfdf5 100%)",
          display: "flex",
          alignItems: "center",
          paddingTop: 72,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Decorative blobs */}
        <div
          style={{
            position: "absolute",
            top: "5%",
            right: "-12%",
            width: 700,
            height: 700,
            background:
              "radial-gradient(circle, rgba(187,247,208,0.55) 0%, transparent 70%)",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: "-8%",
            left: "-6%",
            width: 500,
            height: 500,
            background:
              "radial-gradient(circle, rgba(220,252,231,0.6) 0%, transparent 70%)",
            pointerEvents: "none",
          }}
        />

        <div
          className="hero-grid"
          style={{
            maxWidth: 1200,
            margin: "0 auto",
            padding: "80px 24px",
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 64,
            alignItems: "center",
            width: "100%",
            position: "relative",
          }}
        >
          {/* Left: Copy */}
          <div style={{ animation: "fadeInUp 0.75s ease both" }}>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                background: "#dcfce7",
                color: "#15803d",
                fontWeight: 700,
                fontSize: 13,
                letterSpacing: 0.5,
                textTransform: "uppercase",
                padding: "6px 16px",
                borderRadius: 999,
                marginBottom: 28,
              }}
            >
              <span>⛳</span> Available Now on iOS &amp; Android
            </div>

            <h1
              style={{
                fontSize: "clamp(36px, 5vw, 64px)",
                fontWeight: 900,
                lineHeight: 1.08,
                marginBottom: 24,
                color: "#111827",
                letterSpacing: "-0.5px",
              }}
            >
              Real Golf.{" "}
              <span style={{ color: "#16a34a" }}>Simple Controls.</span> Quick
              Fun.
            </h1>

            <p
              style={{
                fontSize: 19,
                lineHeight: 1.7,
                color: "#4b5563",
                marginBottom: 40,
                maxWidth: 460,
              }}
            >
              The ultimate mobile golf game. Drag to aim, swipe down to swing,
              and experience realistic strategy — without the complexity. Play a
              full 18 holes in minutes.
            </p>

            <div style={{ marginBottom: 48 }}>
              <StoreButtons size={56} />
            </div>

            {/* Quick-stats strip */}
            <div
              style={{
                display: "inline-flex",
                borderRadius: 16,
                overflow: "hidden",
                border: "1.5px solid #e5e7eb",
              }}
            >
              {[
                { num: "10 min", label: "Full 18 holes" },
                { num: "Swipe", label: "To swing" },
                { num: "Free", label: "To download" },
              ].map(({ num, label }, i) => (
                <div
                  key={i}
                  style={{
                    padding: "14px 26px",
                    background: i % 2 === 0 ? "#fff" : "#f9fafb",
                    borderRight: i < 2 ? "1.5px solid #e5e7eb" : "none",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      fontSize: 22,
                      fontWeight: 800,
                      color: "#16a34a",
                      lineHeight: 1,
                    }}
                  >
                    {num}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: "#9ca3af",
                      marginTop: 5,
                      fontWeight: 500,
                    }}
                  >
                    {label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Floating phone with video */}
          <div
            className="hero-phone"
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              position: "relative",
            }}
          >
            {/* Glow behind phone */}
            <div
              style={{
                position: "absolute",
                inset: -60,
                background:
                  "radial-gradient(circle, rgba(134,239,172,0.35) 0%, transparent 65%)",
                pointerEvents: "none",
              }}
            />
            <div
              style={{
                animation: "float 6s ease-in-out infinite",
                position: "relative",
              }}
            >
              {/* Carousel */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 24,
                }}
              >
                <button
                  onClick={() =>
                    setCurrent(
                      (i) => (i - 1 + SCREENSHOTS.length) % SCREENSHOTS.length,
                    )
                  }
                  className="gallery-arrow"
                  aria-label="Previous"
                >
                  ‹
                </button>

                <div style={{ position: "relative" }}>
                  <div
                    style={{
                      position: "absolute",
                      inset: -32,
                      background:
                        "radial-gradient(circle, rgba(74,222,128,0.18) 0%, transparent 70%)",
                      pointerEvents: "none",
                    }}
                  />
                  <PhoneMockup width={240}>
                    <img
                      key={current}
                      src={SCREENSHOTS[current]}
                      alt={`App screenshot ${current + 1}`}
                      style={{
                        width: "100%",
                        display: "block",
                        animation: "fadeIn 0.35s ease",
                      }}
                      loading="lazy"
                    />
                  </PhoneMockup>
                </div>

                <button
                  onClick={() =>
                    setCurrent((i) => (i + 1) % SCREENSHOTS.length)
                  }
                  className="gallery-arrow"
                  aria-label="Next"
                >
                  ›
                </button>
              </div>
              {/* <PhoneMockup width={265}>
                <video
                  ref={videoRef}
                  src="/smplPreview002.mp4"
                  style={{ width: "100%", display: "block" }}
                  autoPlay
                  muted
                  loop
                  playsInline
                />
              </PhoneMockup> */}
            </div>
          </div>
        </div>
      </section>

      {/* ========== FEATURES ========== */}
      <section
        id="features"
        style={{ background: "#ffffff", padding: "100px 24px" }}
      >
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 64 }}>
            <h2
              style={{
                fontSize: "clamp(30px, 4vw, 46px)",
                fontWeight: 900,
                marginBottom: 16,
                color: "#111827",
                letterSpacing: "-0.3px",
              }}
            >
              Why Golfers Love SMPL
            </h2>
            <p
              style={{
                fontSize: 18,
                color: "#6b7280",
                maxWidth: 520,
                margin: "0 auto",
                lineHeight: 1.65,
              }}
            >
              All the depth of real golf, built for your phone. Pick up in
              seconds, keep coming back for hours.
            </p>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: 20,
            }}
          >
            {FEATURES.map(({ icon, title, body, bg }) => (
              <div key={title} className="feature-card">
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 58,
                    height: 58,
                    background: bg,
                    borderRadius: 18,
                    fontSize: 28,
                    marginBottom: 18,
                  }}
                >
                  {icon}
                </div>
                <h3
                  style={{
                    fontSize: 19,
                    fontWeight: 700,
                    marginBottom: 8,
                    color: "#111827",
                  }}
                >
                  {title}
                </h3>
                <p
                  style={{
                    fontSize: 15,
                    lineHeight: 1.65,
                    color: "#6b7280",
                    margin: 0,
                  }}
                >
                  {body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== GALLERY ========== */}
      <section
        id="gallery"
        style={{
          background: "linear-gradient(180deg, #14532d 0%, #166534 100%)",
          padding: "100px 24px",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Texture overlay */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage:
              "radial-gradient(circle at 15% 85%, rgba(255,255,255,0.04) 0%, transparent 40%), radial-gradient(circle at 85% 15%, rgba(255,255,255,0.04) 0%, transparent 40%)",
            pointerEvents: "none",
          }}
        />

        <div
          style={{
            maxWidth: 900,
            margin: "0 auto",
            textAlign: "center",
            position: "relative",
          }}
        >
          <h2
            style={{
              fontSize: "clamp(30px, 4vw, 44px)",
              fontWeight: 900,
              color: "#ffffff",
              marginBottom: 12,
              letterSpacing: "-0.3px",
            }}
          >
            See It In Action
          </h2>
          <p style={{ fontSize: 18, color: "#86efac", marginBottom: 56 }}>
            Stunning courses. Real strategy. Pure mobile fun.
          </p>

          {/* Carousel */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 24,
            }}
          >
            <button
              onClick={() =>
                setCurrent(
                  (i) => (i - 1 + SCREENSHOTS.length) % SCREENSHOTS.length,
                )
              }
              className="gallery-arrow"
              aria-label="Previous"
            >
              ‹
            </button>

            <div style={{ position: "relative" }}>
              <div
                style={{
                  position: "absolute",
                  inset: -32,
                  background:
                    "radial-gradient(circle, rgba(74,222,128,0.18) 0%, transparent 70%)",
                  pointerEvents: "none",
                }}
              />
              <PhoneMockup width={240}>
                <img
                  key={current}
                  src={SCREENSHOTS[current]}
                  alt={`App screenshot ${current + 1}`}
                  style={{
                    width: "100%",
                    display: "block",
                    animation: "fadeIn 0.35s ease",
                  }}
                  loading="lazy"
                />
              </PhoneMockup>
            </div>

            <button
              onClick={() => setCurrent((i) => (i + 1) % SCREENSHOTS.length)}
              className="gallery-arrow"
              aria-label="Next"
            >
              ›
            </button>
          </div>

          {/* Dot indicators */}
          <div
            style={{
              display: "flex",
              gap: 8,
              justifyContent: "center",
              marginTop: 36,
            }}
          >
            {SCREENSHOTS.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrent(i)}
                aria-label={`Go to screenshot ${i + 1}`}
                style={{
                  width: i === current ? 28 : 8,
                  height: 8,
                  borderRadius: 999,
                  background:
                    i === current ? "#4ade80" : "rgba(255,255,255,0.25)",
                  border: 0,
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  padding: 0,
                }}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ========== CTA DOWNLOAD BANNER ========== */}
      <section
        id="download"
        style={{
          background: "linear-gradient(135deg, #16a34a 0%, #15803d 100%)",
          padding: "100px 24px",
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage:
              "radial-gradient(ellipse at 20% 60%, rgba(255,255,255,0.08) 0%, transparent 50%), radial-gradient(ellipse at 80% 30%, rgba(255,255,255,0.06) 0%, transparent 45%)",
            pointerEvents: "none",
          }}
        />
        <div style={{ position: "relative", maxWidth: 680, margin: "0 auto" }}>
          <div style={{ fontSize: 60, marginBottom: 20 }}>⛳</div>
          <h2
            style={{
              fontSize: "clamp(32px, 5vw, 52px)",
              fontWeight: 900,
              color: "#ffffff",
              marginBottom: 16,
              lineHeight: 1.1,
              letterSpacing: "-0.5px",
            }}
          >
            Stop Swiping.
            <br />
            Start Swinging.
          </h2>
          <p
            style={{
              fontSize: 20,
              color: "#dcfce7",
              marginBottom: 48,
              lineHeight: 1.6,
            }}
          >
            Download SMPL Golf today and play your first round in 10 minutes.
          </p>
          <div
            style={{
              display: "flex",
              gap: 16,
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            {[
              {
                href: APPLE_URL,
                src: "/apple-badge.png",
                alt: "Download on the App Store",
              },
              {
                href: GOOGLE_URL,
                src: "/google-play-badge.png",
                alt: "Get it on Google Play",
              },
            ].map(({ href, src, alt }) => (
              <a
                key={href}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="cta-store-btn"
              >
                <img
                  src={src}
                  alt={alt}
                  style={{ height: 52, display: "block" }}
                />
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ========== EMAIL SIGNUP ========== */}
      <section
        id="email"
        style={{
          background: "#f9fafb",
          padding: "88px 24px",
          textAlign: "center",
        }}
      >
        <div style={{ maxWidth: 520, margin: "0 auto" }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>📬</div>
          <h2
            style={{
              fontSize: "clamp(26px, 3vw, 36px)",
              fontWeight: 800,
              marginBottom: 12,
              color: "#111827",
            }}
          >
            Stay in the Loop
          </h2>
          <p
            style={{
              fontSize: 16,
              color: "#6b7280",
              marginBottom: 36,
              lineHeight: 1.65,
            }}
          >
            Be the first to hear about new courses, global competitions, and
            feature drops.
          </p>

          <div
            style={{
              display: "flex",
              gap: 10,
              maxWidth: 460,
              margin: "0 auto 16px",
            }}
          >
            <input
              type="email"
              placeholder="your@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleUpsert()}
              className="email-input"
              style={{
                flex: 1,
                padding: "14px 16px",
                border: "2px solid #e5e7eb",
                borderRadius: 12,
                fontSize: 16,
                outline: "none",
                fontFamily: "inherit",
                transition: "border-color 0.2s",
                background: "#fff",
              }}
            />
            <button
              onClick={handleUpsert}
              className="signup-btn"
              style={{
                padding: "14px 22px",
                background: "#16a34a",
                color: "#fff",
                border: 0,
                borderRadius: 12,
                fontWeight: 700,
                fontSize: 16,
                cursor: "pointer",
                fontFamily: "inherit",
                whiteSpace: "nowrap",
                transition: "background 0.2s, transform 0.15s",
              }}
            >
              Sign Up
            </button>
          </div>

          {status && (
            <div
              role="status"
              style={{
                fontSize: 15,
                fontWeight: 500,
                color: status.type === "ok" ? "#16a34a" : "#dc2626",
                padding: "10px 18px",
                background: status.type === "ok" ? "#f0fdf4" : "#fef2f2",
                borderRadius: 10,
                display: "inline-block",
                border: `1px solid ${status.type === "ok" ? "#bbf7d0" : "#fecaca"}`,
              }}
            >
              {status.msg}
            </div>
          )}
        </div>
      </section>

      {/* ========== LEGAL ========== */}
      <section
        style={{
          background: "#ffffff",
          padding: "48px 24px",
          borderTop: "1px solid #f3f4f6",
        }}
      >
        <div style={{ maxWidth: 860, margin: "0 auto" }}>
          <details style={{ marginBottom: 0 }}>
            <summary className="legal-summary">
              <span className="legal-arrow">▶</span> Terms of Service
            </summary>
            <div className="legal-body">
              <p>
                By using SMPL Golf, you agree to abide by our community
                standards, fair play expectations, and all applicable usage
                terms. Your use of the app constitutes acceptance of these
                terms.
              </p>

              <h3>1. Acceptance of Terms</h3>
              <p>
                By accessing or using the App, you acknowledge that you have
                read, understood, and agree to be bound by these Terms,
                including our Privacy Policy, which is incorporated by
                reference.
              </p>

              <h3>2. Changes to Terms</h3>
              <p>
                We reserve the right to modify these Terms at any time. Any
                changes will be effective immediately upon posting, and your
                continued use of the App will constitute your acceptance of such
                changes. Please review these Terms regularly.
              </p>

              <h3>3. License and Access</h3>
              <p>
                Subject to your compliance with these Terms, we grant you a
                limited, non-exclusive, non-transferable, and revocable license
                to use the App for personal, non-commercial purposes. You may
                not reproduce, distribute, modify, create derivative works of,
                publicly display, or perform the App, except as expressly
                permitted by us.
              </p>

              <h3>4. User Content</h3>
              <p>
                You may submit content, including but not limited to text,
                images, and gameplay data, to the App. By submitting content,
                you grant us a worldwide, royalty-free, perpetual, irrevocable,
                and sublicensable right to use, reproduce, adapt, publish,
                distribute, and display that content in any medium.
              </p>

              <h3>5. In-App Purchases</h3>
              <p>
                The App may offer in-app purchases. By making in-app purchases,
                you agree to pay all associated charges and fees. In-app
                purchases are non-refundable.
              </p>

              <h3>6. Termination</h3>
              <p>
                We reserve the right to terminate or suspend your access to the
                App at our discretion, without notice, for any reason, including
                a violation of these Terms.
              </p>

              <h3>7. Disclaimers and Limitation of Liability</h3>
              <p>
                The App is provided "as is" without warranties of any kind, and
                we do not guarantee its accuracy, reliability, or availability.
                To the extent permitted by law, we shall not be liable for any
                indirect, incidental, special, or consequential damages arising
                out of or in connection with your use of the App.
              </p>

              <h3>8. Governing Law</h3>
              <p>
                These Terms are governed by and construed in accordance with the
                laws of the Commonwealth of Massachusetts. Any disputes arising
                from or related to these Terms shall be subject to the exclusive
                jurisdiction of the courts in the Commonwealth of Massachusetts.
              </p>

              <h3>9. Contact Information</h3>
              <p>
                If you have any questions or concerns about these Terms, please
                contact us at{" "}
                <a href="mailto:smplgolfgame@gmail.com">
                  smplgolfgame@gmail.com
                </a>
                .
              </p>
            </div>
          </details>

          <details id="privacy" style={{ marginTop: 4 }}>
            <summary className="legal-summary">
              <span className="legal-arrow">▶</span> Privacy Policy
            </summary>
            <div className="legal-body">
              <p>
                <strong>Last Updated:</strong> September 18, 2023
              </p>

              <h3>Introduction</h3>
              <p>
                Welcome to SMPL Golf. We are committed to protecting your
                personal information and your right to privacy. If you have any
                questions or concerns, please contact us at{" "}
                <a href="mailto:smplgolfgame@gmail.com">
                  smplgolfgame@gmail.com
                </a>
                .
              </p>

              <h3>Information We Collect</h3>
              <p>
                <strong>Personal Information You Provide:</strong> Usernames and
                email addresses when you register. Payment data is processed by
                our third-party payment gateway — we do not store payment
                details.
              </p>
              <p>
                <strong>Automatically Collected:</strong> Game progress and
                activity (play history, levels completed, game statistics), and
                device information (hardware model, OS version, unique device
                identifiers, mobile network information).
              </p>

              <h3>Use of Information</h3>
              <p>
                We use the information we collect to provide, operate, and
                maintain the Game; manage and remember your game progress;
                manage user accounts; and comply with our legal obligations and
                resolve disputes.
              </p>

              <h3>Your Rights (GDPR)</h3>
              <p>
                Under the GDPR you have the right to access, rectify, erase,
                restrict processing of, and port your personal data. Contact us
                at{" "}
                <a href="mailto:smplgolfgame@gmail.com">
                  smplgolfgame@gmail.com
                </a>{" "}
                to exercise any of these rights.
              </p>

              <h3>Data Retention &amp; Security</h3>
              <p>
                We retain your personal information as long as necessary to
                fulfill the purposes outlined in this Privacy Policy unless a
                longer retention period is required by law. We use various
                security measures including encryption and secure servers to
                protect your information.
              </p>

              <h3>Children's Privacy</h3>
              <p>
                Our Game is not intended for use by children under the age of
                13, and we do not knowingly collect personal information from
                children under 13.
              </p>

              <h3>Changes to This Privacy Policy</h3>
              <p>
                We may change this Privacy Policy from time to time. If we make
                changes, we will notify you by revising the "Last Updated" date
                at the top of this policy.
              </p>

              <h3>Contact Us</h3>
              <p>
                If you have any questions, please contact us at{" "}
                <a href="mailto:smplgolfgame@gmail.com">
                  smplgolfgame@gmail.com
                </a>
                .
              </p>
            </div>
          </details>
        </div>
      </section>

      {/* ========== FOOTER ========== */}
      <footer
        style={{
          background: "#111827",
          color: "#9ca3af",
          padding: "56px 24px 40px",
          textAlign: "center",
        }}
      >
        <img
          src="/smplLogo.png"
          alt="SMPL Golf"
          style={{
            height: 44,
            marginBottom: 24,
            // filter: "brightness(0) invert(1)",
            opacity: 0.75,
          }}
        />
        <div
          style={{
            display: "flex",
            gap: 24,
            justifyContent: "center",
            flexWrap: "wrap",
            marginBottom: 32,
            fontSize: 15,
          }}
        >
          <button onClick={() => scrollTo("features")} className="footer-link">
            Features
          </button>
          <button onClick={() => scrollTo("gallery")} className="footer-link">
            Gallery
          </button>
          <button onClick={() => scrollTo("email")} className="footer-link">
            Updates
          </button>
          <a
            href={APPLE_URL}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#9ca3af", textDecoration: "none", fontSize: 15 }}
          >
            App Store
          </a>
          <a
            href={GOOGLE_URL}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#9ca3af", textDecoration: "none", fontSize: 15 }}
          >
            Google Play
          </a>
        </div>
        <div
          style={{
            borderTop: "1px solid #1f2937",
            paddingTop: 28,
            fontSize: 13,
            display: "flex",
            gap: 16,
            justifyContent: "center",
            flexWrap: "wrap",
            color: "#6b7280",
          }}
        >
          <span>© {new Date().getFullYear()} SMPL Golf</span>
          <span>·</span>
          <a
            href="mailto:smplgolfgame@gmail.com"
            style={{ color: "#6b7280", textDecoration: "none" }}
          >
            smplgolfgame@gmail.com
          </a>
        </div>
      </footer>

      <style>{globalCss}</style>
    </div>
  );
}

// ===========================
// GLOBAL CSS
// ===========================
const globalCss = `
  *, *::before, *::after { box-sizing: border-box; }
  body { margin: 0; -webkit-font-smoothing: antialiased; }
  html { scroll-behavior: smooth; }

  /* Navbar */
  .nav-btn {
    background: none;
    border: 0;
    cursor: pointer;
    font-size: 15px;
    font-weight: 500;
    color: #374151;
    padding: 8px 4px;
    font-family: inherit;
    transition: color 0.2s;
  }
  .nav-btn:hover { color: #16a34a; }

  .cta-pill {
    background: #16a34a;
    color: #fff !important;
    padding: 10px 22px;
    border-radius: 10px;
    text-decoration: none;
    font-weight: 700;
    font-size: 15px;
    transition: background 0.2s, transform 0.15s;
    display: inline-block;
    opacity: 1 !important;
  }
  .cta-pill:hover { background: #15803d; transform: translateY(-1px); }

  /* Feature cards */
  .feature-card {
    background: #fafafa;
    border-radius: 20px;
    padding: 28px 24px;
    border: 1.5px solid #f3f4f6;
    cursor: default;
    transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease;
  }
  .feature-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 18px 44px rgba(0,0,0,0.08);
    border-color: #d1fae5;
  }

  /* Gallery arrows */
  .gallery-arrow {
    background: rgba(255,255,255,0.1);
    backdrop-filter: blur(8px);
    border: 1px solid rgba(255,255,255,0.2);
    color: #fff;
    border-radius: 999px;
    width: 52px;
    height: 52px;
    font-size: 28px;
    cursor: pointer;
    display: grid;
    place-items: center;
    flex-shrink: 0;
    transition: background 0.2s;
  }
  .gallery-arrow:hover { background: rgba(255,255,255,0.22); }

  /* CTA download store buttons */
  .cta-store-btn {
    display: block;
    background: rgba(255,255,255,0.12);
    border-radius: 14px;
    padding: 10px 14px;
    border: 1.5px solid rgba(255,255,255,0.3);
    transition: background 0.2s, transform 0.15s;
    opacity: 1 !important;
  }
  .cta-store-btn:hover { background: rgba(255,255,255,0.22); transform: translateY(-2px); }

  /* Email */
  .email-input:focus { border-color: #16a34a !important; }
  .signup-btn:hover { background: #15803d !important; transform: translateY(-1px); }

  /* Footer */
  .footer-link {
    background: none;
    border: 0;
    color: #9ca3af;
    cursor: pointer;
    font-size: 15px;
    font-family: inherit;
    transition: color 0.2s;
  }
  .footer-link:hover { color: #d1d5db; }

  /* Legal */
  .legal-summary {
    cursor: pointer;
    font-size: 15px;
    font-weight: 600;
    color: #374151;
    padding: 14px 0;
    list-style: none;
    display: flex;
    align-items: center;
    gap: 10px;
    border-bottom: 1px solid #f3f4f6;
    user-select: none;
  }
  .legal-summary::-webkit-details-marker { display: none; }
  details[open] .legal-arrow { transform: rotate(90deg) !important; }
  .legal-arrow {
    font-size: 11px;
    color: #9ca3af;
    display: inline-block;
    transition: transform 0.2s;
  }
  .legal-body {
    padding: 20px 0 8px;
    color: #4b5563;
    font-size: 14px;
    line-height: 1.75;
  }
  .legal-body p { margin-bottom: 14px; }
  .legal-body h3 { font-weight: 700; color: #111827; margin: 20px 0 6px; font-size: 14px; }
  .legal-body a { color: #16a34a; text-decoration: none; }
  .legal-body a:hover { text-decoration: underline; }

  /* Animations */
  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(32px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes fadeIn {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50%       { transform: translateY(-18px); }
  }

  /* Focus rings */
  button:focus-visible, a:focus-visible, input:focus-visible {
    outline: 2px solid #16a34a;
    outline-offset: 2px;
  }

  /* ===== RESPONSIVE ===== */
  @media (max-width: 780px) {
    .hero-grid {
      grid-template-columns: 1fr !important;
      gap: 52px !important;
      padding: 60px 20px 80px !important;
    }
    .hero-phone {
      order: -1;
    }
    .nav-links .nav-btn {
      display: none;
    }
  }
`;
